import React, { FC, useEffect, memo, MouseEvent, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useQueryParam } from 'use-query-params';
import { useMutation, useQuery } from '@apollo/client';

import Song from 'components/songs/Song';
import IconButton from 'components/buttons/IconButton';
import NoSongsFound from 'components/emptyStates/NoSongsFound';
import List, { DragResponse } from 'vibo-ui/List';

import client from 'graphql/client';
import { onError } from 'graphql/helpers';
import { toastNotify } from 'graphql/hooks/common';
import { useSongPlayBar } from 'components/context/SongPlayBarContext';

import { GET_SONG_IDEAS_SONGS } from 'graphql/queries/songIdeas';
import { REMOVE_SONG_FROM_SONG_IDEAS, REORDER_SONG_IDEAS_SONGS } from 'graphql/mutations/songIdeas';

import { ButtonIconType } from 'types/enums';
import { PlaylistSongsProps } from './interfaces';

import useDecorStyle from 'components/buttons/IconButton/decorStyle';
import useStyles from './style';

const PlaylistSongs: FC<PlaylistSongsProps> = ({
  songIdeasId,
  isMySongIdeas,
  isSpotifySyncEnabled,
  onSongsCountUpdate,
}) => {
  const decorClasses = useDecorStyle();
  const classes = useStyles();
  const { t } = useTranslation();

  const isBusy = useRef(false);

  const [playlistSongsQ = '', setPlaylistSongsQ] = useQueryParam<string | undefined>(
    'playlistSongsQ'
  );

  const filter = {
    q: playlistSongsQ,
  };

  const { data: songData, fetchMore, loading: songsLoading } = useQuery<SongIdeasSongsResponse>(
    GET_SONG_IDEAS_SONGS,
    {
      variables: {
        filter,
        songIdeasId,
        pagination: { skip: 0, limit: 50 },
      },
      fetchPolicy: 'cache-and-network',
      onError,
    }
  );
  const songs: SongData[] = songData?.getSongIdeasSongs.songs || [];
  const songsTotalCount = songData?.getSongIdeasSongs?.totalCount || 0;
  const playlistHeaderSongsCount = !!playlistSongsQ ? songs.length : songsTotalCount;

  const [reorderSongIdeasSongs] = useMutation(REORDER_SONG_IDEAS_SONGS, {
    onError,
    refetchQueries: ['getSongIdeasSongs'],
  });

  const [removeSongFromSongIdeas] = useMutation(REMOVE_SONG_FROM_SONG_IDEAS, {
    onCompleted: () => {
      toastNotify({
        text: t('songRemovedFromPlaylistSuccessfully'),
        iconType: ButtonIconType.delete,
      });
    },
    refetchQueries: ['getSongIdeasById', 'getSongIdeasSongs'],
    onError,
  });

  const removeSong = (viboSongId: string) =>
    removeSongFromSongIdeas({
      variables: {
        songIdeasId,
        viboSongId,
      },
    });

  const handleDragChange = (
    reorderedSongs: SongData[],
    { removedIndex, addedIndex }: DragResponse
  ) => {
    if (!isEqual(reorderedSongs, songs)) {
      const targetIndex = addedIndex - (addedIndex < removedIndex ? 1 : 0);

      const sourceSongId = songs[removedIndex]?.viboSongId || null;
      const targetSongId = songs[targetIndex]?.viboSongId || null;

      reorderSongIdeasSongs({
        variables: { songIdeasId, sourceSongId, targetSongId },
      });

      client.writeQuery({
        query: GET_SONG_IDEAS_SONGS,
        variables: {
          filter: { q: '' },
          songIdeasId,
          pagination: { skip: 0, limit: 50 },
        },
        data: {
          getSongIdeasSongs: {
            ...songData?.getSongIdeasSongs,
            songs: reorderedSongs,
          },
        },
      });
    }
  };
  const moveSongUp = (e: MouseEvent<Element>, song: SongData, index: number) => {
    e.stopPropagation();
    handleDragChange([song, ...songs.filter(({ viboSongId }) => viboSongId !== song.viboSongId)], {
      removedIndex: index,
      addedIndex: 0,
    });
  };
  const moveSongDown = (e: MouseEvent<Element>, song: SongData, index: number) => {
    e.stopPropagation();
    handleDragChange([...songs.filter(({ viboSongId }) => viboSongId !== song.viboSongId), song], {
      removedIndex: index,
      addedIndex: songs.length - 1,
    });
  };

  const hasMore =
    !!songData?.getSongIdeasSongs.next &&
    !songsLoading &&
    !!songData?.getSongIdeasSongs.songs.length;

  const loadMore = () => {
    if (!isBusy.current && hasMore) {
      isBusy.current = true;

      fetchMore({
        variables: {
          filter,
          pagination: { ...songData?.getSongIdeasSongs.next, limit: 50 },
        },
        updateQuery: (prev: SongIdeasSongsResponse, { fetchMoreResult }) => {
          isBusy.current = false;

          if (!fetchMoreResult) return prev;

          return {
            getSongIdeasSongs: {
              ...prev.getSongIdeasSongs,
              songs: [...prev.getSongIdeasSongs.songs, ...fetchMoreResult.getSongIdeasSongs.songs],
              next: fetchMoreResult.getSongIdeasSongs.next,
            },
          };
        },
      });
    }
  };
  const player = useSongPlayBar();

  useEffect(() => {
    onSongsCountUpdate?.(playlistHeaderSongsCount);
  }, [playlistHeaderSongsCount]);

  useEffect(() => {
    setPlaylistSongsQ(undefined);

    return () => setPlaylistSongsQ(undefined);
  }, []);

  const songsExtended = songs.map(song => ({
    ...song,
    _id: song.viboSongId,
  }));

  return (
    <>
      {!songs.length && !songsLoading ? (
        <NoSongsFound size="default">
          {t(!!playlistSongsQ ? 'songsNotFound' : 'addSongs')}
        </NoSongsFound>
      ) : null}
      <List
        renderItem={(song, index) => (
          <Song
            song={song}
            index={index}
            onClick={() =>
              player.playSong({ track: { ...song, _id: song.viboSongId }, tracks: songsExtended })
            }
            extra={
              <>
                {isMySongIdeas && !isSpotifySyncEnabled ? (
                  <>
                    <IconButton
                      type={ButtonIconType.arrowUp}
                      className={decorClasses.highlighted}
                      placement="top"
                      title={t('moveToTop')}
                      onClick={e => moveSongUp(e, song, index)}
                    />
                    <IconButton
                      type={ButtonIconType.arrowDown}
                      className={decorClasses.highlighted}
                      placement="top"
                      title={t('moveToBottom')}
                      onClick={e => moveSongDown(e, song, index)}
                    />
                    <IconButton
                      type={ButtonIconType.delete}
                      className={decorClasses.highlighted}
                      placement="top"
                      title={t('deleteSong')}
                      onClick={e => {
                        e.stopPropagation();

                        return removeSong(song.viboSongId!);
                      }}
                    />
                  </>
                ) : null}
              </>
            }
            className={classes.songIdeasSong}
          />
        )}
        data={songsExtended}
        hasMore={hasMore}
        loadMore={loadMore}
        draggable={!isSpotifySyncEnabled}
        onDropCallback={handleDragChange}
      />
    </>
  );
};

export default memo(PlaylistSongs);
